/**
 * @voltxampere/ui — WaveformViewport (Sprint 15)
 *
 * Time × voltage axes for the oscilloscope panel. The schematic
 * Viewport is a single XY camera (`center` + `zoom`); the
 * waveform's two axes scale independently — time base is shared
 * across all channels (one master sweep), V/div is per-channel
 * (each trace owns its vertical scale + offset). The viewport
 * exposes signal-driven primitives so the renderer's `effect()`
 * subscription re-runs on every camera mutation.
 *
 * Sprint 15 (Karar 5): time base + V/div snap to standard scope
 * values. Mouse wheel `scaleTimeBase('in' | 'out')` steps the
 * scope value index; alt + wheel `scaleVDiv(channelId, dir)`
 * steps the active channel's vertical.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
import { signal } from "@preact/signals-core";
import { snapTimeBase, stepTimeBase, MIN_TIME_BASE_S_PER_DIV } from "./time-base.js";
import { snapVDiv, stepVDiv, autoFitVDiv } from "./v-div.js";
import { CHANNEL_IDS, TIME_DIVISION_COUNT, V_DIVISION_COUNT } from "./types.js";
const DEFAULT_TIME_BASE_S_PER_DIV = 1e-3; // 1 ms/div is the comfortable RC default.
const DEFAULT_V_DIV = 1; // 1 V/div per channel.
export class WaveformViewport {
    /** Time per major horizontal division, seconds. */
    timeBase = signal(DEFAULT_TIME_BASE_S_PER_DIV);
    /** Horizontal pan, seconds (left edge = `timeOffset - 5 × timeBase`). */
    timeOffset = signal(0);
    /** Per-channel vertical state. Maps stay live so consumers can
     *  pick out a single channel without iterating; the wrapping
     *  signals (`vDivVersion` / `vOffsetVersion`) bump on any write
     *  so the renderer effect rebuilds the visible scene. */
    vDivByChannel = new Map();
    vOffsetByChannel = new Map();
    vDivVersion = signal(0);
    vOffsetVersion = signal(0);
    constructor() {
        for (const id of CHANNEL_IDS) {
            this.vDivByChannel.set(id, DEFAULT_V_DIV);
            this.vOffsetByChannel.set(id, 0);
        }
    }
    /**
     * Map a sample timestamp (seconds) to a CSS-pixel x coordinate
     * inside a `canvasWidth`-wide canvas. The visible window is
     * `divCount` divisions centred on `timeOffset`.
     */
    timeToScreen(t, canvasWidth, divCount = TIME_DIVISION_COUNT) {
        const tb = this.timeBase.value;
        const tOff = this.timeOffset.value;
        const halfWindow = (tb * divCount) / 2;
        const left = tOff - halfWindow;
        const range = halfWindow * 2;
        if (range <= 0)
            return canvasWidth / 2;
        return ((t - left) / range) * canvasWidth;
    }
    screenToTime(x, canvasWidth, divCount = TIME_DIVISION_COUNT) {
        const tb = this.timeBase.value;
        const tOff = this.timeOffset.value;
        const halfWindow = (tb * divCount) / 2;
        const left = tOff - halfWindow;
        const range = halfWindow * 2;
        return left + (x / canvasWidth) * range;
    }
    /**
     * Map a sample voltage to a CSS-pixel y coordinate. Channel
     * vertical: 0 V sits at the canvas centre (offset 0); positive
     * V points up. The vertical extent is `divCount × vDiv` volts.
     */
    voltageToScreen(v, channelId, canvasHeight, divCount = V_DIVISION_COUNT) {
        const vDiv = this.vDivByChannel.get(channelId) ?? DEFAULT_V_DIV;
        const vOff = this.vOffsetByChannel.get(channelId) ?? 0;
        const halfWindow = (vDiv * divCount) / 2;
        const range = halfWindow * 2;
        if (range <= 0)
            return canvasHeight / 2;
        // Positive volts pointing UP → invert y.
        return ((halfWindow - (v - vOff)) / range) * canvasHeight;
    }
    // ── time base mutation ──────────────────────────────────────────
    setTimeBase(seconds) {
        const next = snapTimeBase(seconds);
        if (next === this.timeBase.value)
            return;
        this.timeBase.value = next;
    }
    scaleTimeBase(direction) {
        const next = stepTimeBase(this.timeBase.value, direction);
        if (next === this.timeBase.value)
            return;
        this.timeBase.value = next;
    }
    setTimeOffset(seconds) {
        if (!Number.isFinite(seconds))
            return;
        if (seconds === this.timeOffset.value)
            return;
        this.timeOffset.value = seconds;
    }
    panTime(deltaSeconds) {
        if (!Number.isFinite(deltaSeconds) || deltaSeconds === 0)
            return;
        this.timeOffset.value = this.timeOffset.value + deltaSeconds;
    }
    // ── per-channel vertical mutation ───────────────────────────────
    getVDiv(id) {
        return this.vDivByChannel.get(id) ?? DEFAULT_V_DIV;
    }
    setVDiv(id, vDiv) {
        const next = snapVDiv(vDiv);
        if (next === this.vDivByChannel.get(id))
            return;
        this.vDivByChannel.set(id, next);
        this.vDivVersion.value = this.vDivVersion.value + 1;
    }
    scaleVDiv(id, direction) {
        const next = stepVDiv(this.vDivByChannel.get(id) ?? DEFAULT_V_DIV, direction);
        if (next === this.vDivByChannel.get(id))
            return;
        this.vDivByChannel.set(id, next);
        this.vDivVersion.value = this.vDivVersion.value + 1;
    }
    getVOffset(id) {
        return this.vOffsetByChannel.get(id) ?? 0;
    }
    setVOffset(id, offset) {
        if (!Number.isFinite(offset))
            return;
        if (offset === this.vOffsetByChannel.get(id))
            return;
        this.vOffsetByChannel.set(id, offset);
        this.vOffsetVersion.value = this.vOffsetVersion.value + 1;
    }
    // ── auto fit ────────────────────────────────────────────────────
    /**
     * Choose the smallest standard time base whose visible window
     * encloses the supplied sample range, and per-channel V/div
     * matching the per-channel peak-to-peak. Per-channel vOffset is
     * reset to 0 so the trace centres on the panel.
     */
    autoFit(sampleRanges, divCount = {}) {
        if (sampleRanges.size === 0)
            return;
        const tDiv = divCount.time ?? TIME_DIVISION_COUNT;
        const vDivCount = divCount.voltage ?? V_DIVISION_COUNT;
        let tStartGlobal = Number.POSITIVE_INFINITY;
        let tEndGlobal = Number.NEGATIVE_INFINITY;
        for (const r of sampleRanges.values()) {
            if (r.tStart < tStartGlobal)
                tStartGlobal = r.tStart;
            if (r.tEnd > tEndGlobal)
                tEndGlobal = r.tEnd;
        }
        const span = Math.max(tEndGlobal - tStartGlobal, MIN_TIME_BASE_S_PER_DIV);
        this.setTimeBase(span / tDiv);
        this.setTimeOffset((tStartGlobal + tEndGlobal) / 2);
        for (const [id, r] of sampleRanges.entries()) {
            const vpp = Math.max(r.vMax - r.vMin, 0);
            this.setVDiv(id, autoFitVDiv(vpp, vDivCount));
            this.setVOffset(id, 0);
        }
    }
}
//# sourceMappingURL=viewport.js.map